/**
 * BLRRD Selection — feather.js  (Phase 4.B.select.2, Wave 8.2)
 *
 * True Euclidean-Distance-Transform feather. Wave 8.1 shipped a Gaussian-
 * blur stub (`featherStub`); this module now adds the proper algorithm:
 *
 *     Meijster, Roerdink, Hesselink (2000) — "A general algorithm for
 *     computing distance transforms in linear time" (Pattern Recognition
 *     34(11), pp. 1907–1912).
 *
 * Two passes, both O(W·H):
 *
 *   1. Vertical (`computeG`): for every column x, find the squared
 *      distance to the nearest background pixel along the column.
 *      Two-sweep "Felzenszwalb-style" propagation: forward scan with
 *      `g[x,y] = min(g[x,y-1]+1, …)` then backward scan.
 *
 *   2. Horizontal (`computeDTRow`): for every row y, treat the column
 *      results as 1-D parabolas `f_x(i) = (i − x)² + g[x,y]²` and find
 *      the lower envelope using a sliding stack of break-points — the
 *      Voronoi-diagram-style algorithm in §3 of the paper. The result
 *      at column i is the squared Euclidean distance to the nearest
 *      background pixel anywhere on the grid.
 *
 * Direction
 * ─────────
 * Selection feather softens INSIDE the selection: a pixel that was fully
 * selected (1.0) and sits exactly on the edge transitions to 0 over the
 * feather radius going outward. We compute the EDT of the *outside* set
 * (mask < 0.5) — then `dist[i]` tells us how far each selected pixel is
 * from the nearest unselected one. That distance is normalised to the
 * feather radius and run through a smoothstep `3t² − 2t³` ramp.
 *
 * For pixels currently OUTSIDE the selection (mask < 0.5) we leave them
 * at 0 (they stay unselected — feather doesn't grow a selection, it
 * softens its inside edge). This matches Photoshop's "Modify → Feather"
 * behaviour on a hard-edge selection.
 *
 * Soft-edge inputs
 * ────────────────
 * The DT operates on a binary threshold (`mask[i] ≥ 0.5`), so passing
 * an already-feathered or anti-aliased mask will discard sub-pixel
 * coverage information at the boundary. That's the intended behaviour —
 * feathering an AA edge a few times shouldn't compound the softness
 * indefinitely. Users who want a softer-than-already-soft selection
 * can stack multiple feather passes; each one is rooted at the binary
 * 0.5 iso-line.
 *
 * Identity short-circuit
 * ──────────────────────
 * Radius ≤ 0 → returns the same `mask` reference unchanged, no work.
 * This is what protects the pixel-hash + worker-parity baselines on
 * tiny fixtures that never invoke feather.
 *
 * Provenance: clean-room from the Meijster 2000 paper. NO GIMP/GEGL/
 * darktable/Krita source consulted. Smooth-step (3t² − 2t³) is the
 * Perlin 1985 / RenderMan classic, also textbook.
 */

/* Sentinel for "no background pixel in this column". The paper uses
 * `infty = W + H`; any value > the maximum possible squared distance
 * works. We square it lazily inside the row pass. */
const INF_G = 1e9;

/**
 * First pass — column-wise propagation. For each column, fill `g[i]`
 * with the unsigned distance (in pixels, integer) from row y to the
 * nearest background pixel in that same column. Background is defined
 * as `mask[i] < threshold`.
 *
 * @param {Float32Array} mask    binary-ish input mask, length = w*h
 * @param {Float32Array} g       output, length = w*h, integer distances
 * @param {number}        w
 * @param {number}        h
 * @param {number}        threshold
 */
function computeG(mask, g, w, h, threshold) {
    /* Forward scan: top → bottom. */
    for (let x = 0; x < w; x++) {
        /* First row. */
        g[x] = (mask[x] < threshold) ? 0 : INF_G;
        for (let y = 1; y < h; y++) {
            const i = y * w + x;
            if (mask[i] < threshold) {
                g[i] = 0;
            } else {
                const above = g[i - w];
                g[i] = (above === INF_G) ? INF_G : above + 1;
            }
        }
    }
    /* Backward scan: bottom → top, take min with one-below + 1. */
    for (let x = 0; x < w; x++) {
        for (let y = h - 2; y >= 0; y--) {
            const i = y * w + x;
            const below = g[i + w];
            if (below !== INF_G) {
                const cand = below + 1;
                if (cand < g[i]) g[i] = cand;
            }
        }
    }
}

/**
 * Second pass — row-wise lower envelope of parabolas. For one row y,
 * fill `dt[y*w + i]` with the squared Euclidean distance to the
 * nearest background pixel anywhere in the image.
 *
 * The "envelope of parabolas" trick is §3 of Meijster 2000: each
 * column x contributes a parabola `f_x(i) = (i − x)² + g[x,y]²` to row
 * y. The per-row EDT is the lower envelope; the algorithm walks the
 * row twice — once forward to build a stack of (column, leftmost-i)
 * pairs, once forward to read off `dt[i]` from the active entry.
 *
 *   f(i, x) = (i − x)² + g[x,y]²
 *   sep(x1, x2) = ⌊((x2² − x1²) + (g[x2,y]² − g[x1,y]²)) / (2·(x2 − x1))⌋
 *
 * @param {Float32Array} g       column-distance array (full w*h)
 * @param {Float32Array} dt      output squared-distance array (full w*h)
 * @param {Int32Array}   s       scratch stack of column indices, length ≥ w
 * @param {Int32Array}   t       scratch leftmost-i positions, length ≥ w
 * @param {number}        w
 * @param {number}        y
 */
function computeDTRow(g, dt, s, t, w, y) {
    const rowBase = y * w;

    /* Skip rows that have NO background pixel at all in their columns —
     * dt stays INF_G (capped by the caller). */
    let anyFinite = false;
    for (let x = 0; x < w; x++) {
        if (g[rowBase + x] !== INF_G) { anyFinite = true; break; }
    }
    if (!anyFinite) {
        for (let i = 0; i < w; i++) dt[rowBase + i] = INF_G;
        return;
    }

    const f = (i, x) => {
        const gx = g[rowBase + x];
        const dx = i - x;
        return dx * dx + gx * gx;
    };

    /* Sep(u, v): leftmost integer column where parabola at v overtakes u.
     * Both u, v are columns with finite g[x,y]. */
    const sep = (u, v) => {
        const gu = g[rowBase + u], gv = g[rowBase + v];
        const num = (v * v - u * u) + (gv * gv - gu * gu);
        const denom = 2 * (v - u);
        return Math.floor(num / denom);
    };

    /* Phase 2a — build envelope (forward scan, push/pop). */
    let q = -1;          /* stack top index */
    for (let x = 0; x < w; x++) {
        if (g[rowBase + x] === INF_G) continue;
        if (q < 0) {
            q = 0;
            s[0] = x;
            t[0] = 0;
            continue;
        }
        /* Pop while the candidate x dominates the top entry over [t[q], …]. */
        while (q >= 0) {
            const u = s[q];
            const w_t = t[q];
            if (f(w_t, u) <= f(w_t, x)) break;   /* top still wins at its left-edge */
            q--;
        }
        if (q < 0) {
            q = 0;
            s[0] = x;
            t[0] = 0;
        } else {
            const w_new = 1 + sep(s[q], x);
            if (w_new < w) {
                q++;
                s[q] = x;
                t[q] = w_new;
            }
        }
    }

    /* Phase 2b — read envelope (forward scan). */
    if (q < 0) {
        /* Defensive — shouldn't happen given the anyFinite check above. */
        for (let i = 0; i < w; i++) dt[rowBase + i] = INF_G;
        return;
    }
    let k = 0;
    for (let i = 0; i < w; i++) {
        while (k < q && t[k + 1] <= i) k++;
        dt[rowBase + i] = f(i, s[k]);
    }
}

/**
 * Compute the Euclidean distance transform of a binary mask. Output is
 * the **squared** Euclidean distance from each pixel to the nearest
 * pixel whose mask value is < `threshold`. The squared form avoids the
 * per-pixel sqrt and is what `featherSelection` consumes directly.
 *
 * Caller owns the input/output buffers. `dt` must have the same length
 * as `mask` (w*h).
 *
 * @param {Float32Array} mask
 * @param {Float32Array} dt           squared-distance output
 * @param {number}        w
 * @param {number}        h
 * @param {number}        [threshold=0.5]
 * @returns {Float32Array}            `dt`
 */
export function distanceTransform(mask, dt, w, h, threshold = 0.5) {
    if (!(mask instanceof Float32Array) || !(dt instanceof Float32Array)) {
        throw new Error('distanceTransform: mask and dt must be Float32Array');
    }
    const W = w | 0, H = h | 0;
    const N = W * H;
    if (mask.length !== N || dt.length !== N) {
        throw new Error(`distanceTransform: length mismatch (mask=${mask.length}, dt=${dt.length}, expected=${N})`);
    }

    /* Phase 1: column distances. */
    const g = new Float32Array(N);
    computeG(mask, g, W, H, threshold);

    /* Phase 2: row envelope-of-parabolas, per row. */
    const stack  = new Int32Array(W);
    const breaks = new Int32Array(W);
    for (let y = 0; y < H; y++) {
        computeDTRow(g, dt, stack, breaks, W, y);
    }
    return dt;
}

/**
 * Smooth-step ramp 3t² − 2t³ (Perlin 1985 / RenderMan). t is clamped
 * to [0, 1] by the caller.
 */
function smoothStep(t) {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    return t * t * (3 - 2 * t);
}

/**
 * Replace `mask` in place with a feathered version. The selection
 * boundary (current iso-line at 0.5) is left in place; pixels INSIDE
 * the selection within `radius` pixels of the boundary ramp from 0 at
 * the boundary up to 1.0 at distance `radius` via a smooth-step
 * (3t² − 2t³) profile.
 *
 * Pixels outside the selection (mask < 0.5) are forced to 0.
 *
 * Radius ≤ 0 → no-op, returns the mask reference unchanged. Identity
 * short-circuit; protects the pixel-hash + worker-parity baselines.
 *
 * @param {Float32Array} mask         selection mask, length = w*h
 * @param {number}        w
 * @param {number}        h
 * @param {number}        radius      feather radius in pixels
 * @returns {Float32Array}            the same `mask` reference
 */
export function featherSelection(mask, w, h, radius) {
    if (!(mask instanceof Float32Array)) {
        throw new Error('featherSelection: mask must be Float32Array');
    }
    if (!Number.isFinite(radius) || radius <= 0) return mask;
    const W = w | 0, H = h | 0;
    if (mask.length !== W * H) {
        throw new Error(`featherSelection: mask length mismatch (${mask.length} vs ${W * H})`);
    }

    const N = W * H;
    const dt = new Float32Array(N);
    distanceTransform(mask, dt, W, H, 0.5);

    /* Edge of mask (mask >= 0.5 with dt²==0) sits at the iso-line. We
     * want a "0 right at the edge, 1 well inside" ramp, so distance/r
     * is the smooth-step argument. The DT is squared distance, so we
     * compare against radius² and take sqrt only for pixels in the band. */
    const r = radius;
    const r2 = r * r;
    for (let i = 0; i < N; i++) {
        if (mask[i] < 0.5) {
            /* Pixel was outside selection — stays outside. */
            mask[i] = 0;
            continue;
        }
        const d2 = dt[i];
        if (d2 >= r2) {
            /* Well inside — fully selected. */
            mask[i] = 1;
        } else {
            const t = Math.sqrt(d2) / r;
            mask[i] = smoothStep(t);
        }
    }
    return mask;
}

/**
 * Back-compat alias for the Wave-8.1 export name. Callers that imported
 * `featherStub` still work, but the implementation now routes through
 * the proper Meijster EDT.
 *
 * @param {Float32Array} mask
 * @param {number}        w
 * @param {number}        h
 * @param {number}        radius
 * @returns {Float32Array}
 */
export function featherStub(mask, w, h, radius) {
    return featherSelection(mask, w, h, radius);
}
